import React, { useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Edit, Eye, Minus, Plus, Trash2 } from "lucide-react";

const QUESTION_COLUMNS =
  "minmax(200px, 2fr) 72px minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) 108px";

const ANSWER_LABEL = {
  option_1: "Option 1",
  option_2: "Option 2",
  option_3: "Option 3",
  option_4: "Option 4",
};

export function isModuleQuestionsListTable(tableName) {
  return String(tableName || "").toLowerCase() === "ems_module_questions";
}

function text(value) {
  const next = String(value ?? "").trim();
  return next && next !== "-" ? next : "-";
}

function mapQuestionRow(item, fallbackKey) {
  const id =
    item?.organization_ems_module_question_id ??
    item?.id ??
    fallbackKey;
  const correct = String(item?.correct_answer || "").trim();
  return {
    key: String(id),
    id,
    organization_ems_module_question_id: id,
    question: text(item?.question),
    option_1: text(item?.option_1),
    option_2: text(item?.option_2),
    option_3: text(item?.option_3),
    option_4: text(item?.option_4),
    correct_answer: ANSWER_LABEL[correct] || correct || "-",
    marks:
      item?.marks === null || item?.marks === undefined || item?.marks === ""
        ? "1"
        : String(item.marks),
  };
}

export function getModuleQuestionGroupRows(row) {
  if (Array.isArray(row?.modules) && row.modules.length) {
    return row.modules.map((mod, index) => {
      const questions = Array.isArray(mod?.questions) ? mod.questions : [];
      const id =
        mod?.organization_ems_training_program_module_id ??
        mod?.id ??
        `${row?.id || "module"}-${index}`;
      return {
        key: String(id),
        id,
        organization_ems_training_program_module_id: id,
        module_name: mod?.module_name || "-",
        question_count: questions.length,
        total_marks:
          mod?.total_marks ??
          questions.reduce((sum, item) => sum + (Number(item?.marks) || 0), 0),
        questions: questions.map((item, qIndex) =>
          mapQuestionRow(item, `${id}-${qIndex}`)
        ),
      };
    });
  }

  const questions = Array.isArray(row?.questions) ? row.questions : [];
  const byModule = new Map();
  questions.forEach((item, index) => {
    const moduleId =
      item?.organization_ems_training_program_module_id ??
      item?.module?.organization_ems_training_program_module_id ??
      item?.module_name ??
      `${row?.id || "module"}-${index}`;
    const key = String(moduleId);
    if (!byModule.has(key)) {
      byModule.set(key, {
        key,
        id: moduleId,
        organization_ems_training_program_module_id: moduleId,
        module_name:
          item?.module_name ||
          item?.module?.module_name ||
          item?.training_program_module?.module_name ||
          "-",
        questions: [],
      });
    }
    byModule.get(key).questions.push(mapQuestionRow(item, `${key}-${index}`));
  });

  return Array.from(byModule.values()).map((mod) => ({
    ...mod,
    question_count: mod.questions.length,
    total_marks: mod.questions.reduce(
      (sum, item) => sum + (Number(item?.marks) || 0),
      0
    ),
  }));
}

function TruncatedCell({ children }) {
  const value = children == null ? "" : String(children);
  return (
    <Box
      title={value}
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
      }}
    >
      {value}
    </Box>
  );
}

function QuestionRows({
  questions,
  colors,
  showViewButton,
  showEditButton,
  showDeleteButton,
  onView,
  onEdit,
  onDelete,
}) {
  if (!questions.length) {
    return (
      <Typography variant="body2" sx={{ px: 1, py: 1, color: colors.text.secondary }}>
        No questions in this module.
      </Typography>
    );
  }

  return questions.map((detail) => (
    <Box
      key={detail.key}
      sx={{
        display: "grid",
        gridTemplateColumns: QUESTION_COLUMNS,
        alignItems: "center",
        gap: 1,
        pr: 1,
        py: 1,
        fontSize: "13px",
        borderTop: `1px dashed ${colors.grey[300]}`,
      }}
    >
      <TruncatedCell>{detail.question}</TruncatedCell>
      <Box sx={{ fontWeight: 600 }}>{detail.marks}</Box>
      <TruncatedCell>{detail.option_1}</TruncatedCell>
      <TruncatedCell>{detail.option_2}</TruncatedCell>
      <TruncatedCell>{detail.option_3}</TruncatedCell>
      <TruncatedCell>{detail.option_4}</TruncatedCell>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
        {showViewButton && (
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onView?.(detail);
              }}
              sx={{ color: colors.text.primary }}
            >
              <Eye size={15} />
            </IconButton>
          </Tooltip>
        )}
        {showEditButton && (
          <Tooltip title="Edit this question">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(detail);
              }}
              sx={{ color: colors.text.primary }}
            >
              <Edit size={15} />
            </IconButton>
          </Tooltip>
        )}
        {showDeleteButton && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(detail);
              }}
              sx={{ color: colors.text.primary }}
            >
              <Trash2 size={15} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  ));
}

export function ModuleQuestionsAccordionCollapseRow({
  expanded,
  colSpan,
  accordionRows,
  colors,
  showViewButton = false,
  showEditButton = false,
  showDeleteButton = false,
  onView,
  onEdit,
  onEditAll,
  onDelete,
  onDeleteAll,
}) {
  const [openModules, setOpenModules] = useState({});

  const toggleModule = (key) => {
    setOpenModules((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 0,
          px: 2,
          borderBottom: `1px solid ${colors.grey[200]}`,
          backgroundColor: colors.grey[50],
        }}
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ py: 1.5, pl: "40px" }}>
            {accordionRows.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ px: 1, color: colors.text.secondary }}
              >
                No modules in this category.
              </Typography>
            ) : (
              accordionRows.map((mod) => {
                const moduleOpen = !!openModules[mod.key];
                const questionLabel =
                  Number(mod.question_count) === 1
                    ? "1 question"
                    : `${mod.question_count} questions`;
                return (
                  <Box
                    key={mod.key}
                    sx={{
                      mb: 1,
                      border: `1px solid ${colors.grey[200]}`,
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => toggleModule(mod.key)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 1,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: colors.grey[50] },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleModule(mod.key);
                        }}
                        sx={{ color: colors.text.primary }}
                      >
                        {moduleOpen ? <Minus size={16} /> : <Plus size={16} />}
                      </IconButton>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "13.5px",
                            fontWeight: 700,
                            color: colors.primary || "var(--ems-primary, #0891B2)",
                          }}
                        >
                          {mod.module_name}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 600,
                          color: colors.text.secondary,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {questionLabel}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12.5px",
                          fontWeight: 700,
                          color: colors.primary || "var(--ems-primary, #0891B2)",
                          whiteSpace: "nowrap",
                          minWidth: 72,
                          textAlign: "right",
                        }}
                      >
                        {mod.total_marks} marks
                      </Typography>
                      {showEditButton && (mod.questions || []).length > 0 ? (
                        <Tooltip title="Edit all questions in this module">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditAll?.(mod);
                            }}
                            sx={{
                              color: colors.primary || "var(--ems-primary, #0891B2)",
                              border: `1px solid ${colors.grey[300]}`,
                              borderRadius: "8px",
                              px: 1,
                              ml: 0.5,
                            }}
                          >
                            <Edit size={15} />
                            <Box sx={{ ml: 0.5, fontSize: "12px", fontWeight: 700 }}>
                              Edit all
                            </Box>
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {showDeleteButton && (mod.questions || []).length > 0 ? (
                        <Tooltip title="Delete all questions in this module">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteAll?.(mod);
                            }}
                            sx={{
                              color: colors.primary || "var(--ems-primary, #0891B2)",
                              border: `1px solid ${colors.grey[300]}`,
                              borderRadius: "8px",
                              px: 1,
                              ml: 0.5,
                            }}
                          >
                            <Trash2 size={15} />
                            <Box sx={{ ml: 0.5, fontSize: "12px", fontWeight: 700 }}>
                              Delete all
                            </Box>
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>

                    <Collapse in={moduleOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 1.5, pb: 1.25, pl: 6 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: QUESTION_COLUMNS,
                            gap: 1,
                            pr: 1,
                            pb: 1,
                            fontSize: "12px",
                            fontWeight: 700,
                            color: colors.text.secondary,
                          }}
                        >
                          <Box>Question</Box>
                          <Box>Marks</Box>
                          <Box>Option 1</Box>
                          <Box>Option 2</Box>
                          <Box>Option 3</Box>
                          <Box>Option 4</Box>
                          <Box sx={{ textAlign: "right" }}>Actions</Box>
                        </Box>
                        <QuestionRows
                          questions={mod.questions || []}
                          colors={colors}
                          showViewButton={showViewButton}
                          showEditButton={showEditButton}
                          showDeleteButton={showDeleteButton}
                          onView={onView}
                          onEdit={onEdit}
                          onDelete={onDelete}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

export function ModuleQuestionsMobileList({
  modules,
  showViewButton = false,
  showEditButton = false,
  showDeleteButton = false,
  onView,
  onEdit,
  onEditAll,
  onDelete,
  onDeleteAll,
  actionBtnSx,
}) {
  const [openModules, setOpenModules] = useState({});

  if (!modules.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        pt: 0.75,
        borderTop: "1px solid var(--ems-border)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {modules.map((mod) => {
        const moduleOpen = !!openModules[mod.key];
        const questions = mod.questions || [];
        const questionLabel =
          Number(mod.question_count) === 1
            ? "1 question"
            : `${mod.question_count} questions`;
        return (
          <Box key={mod.key} sx={{ minWidth: 0 }}>
            <Box
              onClick={() =>
                setOpenModules((prev) => ({ ...prev, [mod.key]: !prev[mod.key] }))
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                cursor: "pointer",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: "var(--ems-text)",
                    wordBreak: "break-word",
                  }}
                >
                  {mod.module_name}
                </Box>
                <Box sx={{ fontSize: "0.72rem", color: "var(--ems-muted)" }}>
                  {questionLabel}
                  {mod.total_marks != null ? ` · ${mod.total_marks} marks` : ""}
                </Box>
              </Box>
              {showEditButton && questions.length > 0 && (
                <Tooltip title="Edit all questions">
                  <IconButton
                    size="small"
                    aria-label="Edit all questions"
                    sx={actionBtnSx}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditAll?.(mod);
                    }}
                  >
                    <Edit size={16} />
                  </IconButton>
                </Tooltip>
              )}
              {showDeleteButton && questions.length > 0 && (
                <Tooltip title="Delete all questions">
                  <IconButton
                    size="small"
                    aria-label="Delete all questions"
                    sx={actionBtnSx}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAll?.(mod);
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {moduleOpen &&
              questions.map((detail) => (
                <Box
                  key={detail.key}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    minWidth: 0,
                    pl: 1,
                    pt: 0.75,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: "0.78rem",
                        color: "var(--ems-text)",
                        wordBreak: "break-word",
                      }}
                    >
                      {detail.question}
                    </Box>
                    <Box sx={{ fontSize: "0.7rem", color: "var(--ems-muted)" }}>
                      {detail.marks} marks · {detail.correct_answer}
                    </Box>
                  </Box>
                  {showViewButton && (
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        aria-label="View"
                        sx={actionBtnSx}
                        onClick={(e) => {
                          e.stopPropagation();
                          onView?.(detail);
                        }}
                      >
                        <Eye size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {showEditButton && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label="Edit"
                        sx={actionBtnSx}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit?.(detail);
                        }}
                      >
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {showDeleteButton && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        sx={actionBtnSx}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete?.(detail);
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
          </Box>
        );
      })}
    </Box>
  );
}
